import { useState, useCallback } from 'react';
import { useAuthStore } from '../../../store/auth.store';
import { isApiError } from '@aiptyg/types';
import * as authService from '../services/auth.service';
import type { LoginRequest } from '../types/auth.types';

const ERROR_MESSAGES: Record<string, string> = {
  invalid_credentials: '邮箱或密码错误',
  'invalid credentials': '邮箱或密码错误',
  user_not_found: '用户不存在',
  'user not found': '用户不存在',
  user_disabled: '账号已被禁用',
  'user disabled': '账号已被禁用',
  captcha_verify_param_required: '需要完成人机验证',
  'captcha verify param is required': '需要完成人机验证',
  'captcha ticket is required': '需要完成人机验证',
  'captcha randstr is required': '需要完成人机验证',
  CAPTCHA_REPLAYED: '验证码已失效，请重新验证',
  'captcha rate limit exceeded': '验证过于频繁，请稍后重试',
  'captcha service unavailable': '验证码服务暂不可用，请稍后重试',
  captcha_verification_failed: '人机验证失败，请重试',
  'captcha verification failed': '人机验证失败，请重试',
  network_error: '网络连接失败，请检查网络',
  admin_role_required: '无管理员权限',
};

function resolveErrorMessage(e: unknown): string {
  if (isApiError(e)) {
    return ERROR_MESSAGES[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession);
  const logout = useAuthStore((s) => s.logout);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const doLogin = useCallback(
    async (req: LoginRequest): Promise<boolean> => {
      setError(null);
      setLoading(true);
      try {
        const session = await authService.login(req);

        // Admin role check — role comes from login session (PascalCase from Go)
        if (session.User.Role !== 'admin') {
          logout();
          setError('无管理员权限，禁止登录后台');
          return false;
        }

        setSession(session);
        return true;
      } catch (e) {
        setError(resolveErrorMessage(e));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [setSession, logout],
  );

  return { doLogin, loading, error };
}

/**
 * Refresh user profile from /v1/users/me.
 * Called on app startup when a persisted token exists.
 * If 401, the api-client triggers global logout automatically.
 */
export function useRefreshProfile() {
  const updateProfile = useAuthStore((s) => s.updateProfile);

  const refresh = useCallback(async () => {
    try {
      const me = await authService.fetchMe();
      updateProfile(me);
    } catch {
      // 401 is handled by api-client's onUnauthorized callback
    }
  }, [updateProfile]);

  return refresh;
}
